/**
 * Dashboard /analytics tab — chart + per-eSIM breakdown.
 *
 * Originally lines 459-768 of `routes/dashboard.ts`. Phase 2.2.
 */
import { escapeHtml } from '../../utils';
import * as api from '../../api';
import { t } from '../../i18n';
import { formatHistoryType } from './history';
import { fmtData, fmtDate, fmtMoney } from '../../utils/format';

/**
 * Lazy-load the country breakdown when the user opens the <details> for an
 * eSIM. We avoid pre-fetching for every card because users rarely open more
 * than one breakdown at a time.
 */
export async function loadBreakdown(esimId: string, days: 7 | 14 | 30 | 90, body: HTMLElement): Promise<void> {
  body.innerHTML = `<p class="dash-sub" data-i18n="dash.esims.breakdownLoading">${escapeHtml(t('dash.esims.breakdownLoading'))}</p>`;
  try {
    const r = await api.fetchUsageByCountry(esimId, days);
    if (r.breakdown.length === 0) {
      body.innerHTML = `<p class="dash-sub" data-i18n="dash.esims.breakdownEmpty">${escapeHtml(t('dash.esims.breakdownEmpty'))}</p>`;
      return;
    }
    const rows = r.breakdown.map((b) => {
      // If we have a non-zero spend but mb=0, that's a legacy row written
      // before venta-sync started embedding deltaMb in ledger.notes —
      // show "—" instead of misleading "0.0 MB". Operator caught the
      // mismatch 2026-05-10 +22.
      const mbCell = b.mb > 0 ? fmtData(b.mb) : (b.micros > 0 ? '—' : fmtData(0));
      return `<tr><td class="mono">${escapeHtml(b.countryCode)}</td><td class="mono">${mbCell}</td><td class="mono">${fmtMoney(b.micros)}</td></tr>`;
    }).join('');
    body.innerHTML = `<table class="dash-esim-card__breakdown-table">
      <thead><tr><th data-i18n="dash.esims.breakdownCountry">${escapeHtml(t('dash.esims.breakdownCountry'))}</th><th data-i18n="dash.esims.breakdownMb">${escapeHtml(t('dash.esims.breakdownMb'))}</th><th data-i18n="dash.esims.breakdownSpent">${escapeHtml(t('dash.esims.breakdownSpent'))}</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  } catch (err) {
    body.innerHTML = `<p class="dash-sub">${escapeHtml(t('dash.esims.breakdownError'))} ${escapeHtml((err as Error).message)}</p>`;
  }
}

/* ─── Analytics panel (placeholder until vapi) ──────────── */

export function analyticsPanel(): string {
  // Real stats — populated by loadAnalyticsInto() against /api/me/stats.
  // The eSIM picker is rendered by loadAnalyticsInto on first load (we
  // don't have the list of eSIMs at SSR-render time; the API response
  // ships availableEsims so the dropdown can be populated dynamically).
  return `
<h2 class="dash-h2">${escapeHtml(t('dash.analytics.title'))}</h2>
<p class="dash-sub" style="margin-bottom:16px;" data-i18n="dash.analytics.subhead">${escapeHtml(t('dash.analytics.subhead'))}</p>
<div class="dash-analytics-controls" style="display:flex;gap:16px;flex-wrap:wrap;align-items:center;margin-bottom:16px">
  <div class="dash-analytics-period" data-analytics-period role="tablist">
    <button class="dash-period-btn" data-days="7">7 ${escapeHtml(t('dash.analytics.daysShort'))}</button>
    <button class="dash-period-btn is-active" data-days="30">30 ${escapeHtml(t('dash.analytics.daysShort'))}</button>
    <button class="dash-period-btn" data-days="90">90 ${escapeHtml(t('dash.analytics.daysShort'))}</button>
  </div>
  <label class="dash-analytics-picker" data-analytics-picker style="display:none">
    <span class="label" data-i18n="dash.analytics.pickerLabel">${escapeHtml(t('dash.analytics.pickerLabel'))}</span>
    <select class="dash-analytics-picker__select" data-analytics-esim-select>
      <!-- options populated dynamically -->
    </select>
  </label>
</div>
<div class="dash-analytics" data-analytics-mount>
  <div class="dash-loading" data-i18n="dash.analytics.loading">${escapeHtml(t('dash.analytics.loading'))}</div>
</div>`;
}

export interface MeStatsResp {
  days: number;
  empty: boolean;
  esimCount: number;
  activeEsimCount: number;
  reservedEsimCount: number;
  currentBalanceMicroUsdt: number;
  dataConsumedMb: number;
  totalSpentMicroUsdt: number;
  totalToppedUpMicroUsdt: number;
  totalBonusMicroUsdt: number;
  byCountry: Array<{ countryCode: string; microUsdt: number; mb: number }>;
  dailySeries: Array<{ date: string; spentMicroUsdt: number; toppedUpMicroUsdt: number }>;
  recentLedger: Array<{ id: string; esimId: string | null; type: string; amountMicroUsdt: number; createdAt: string; notes: string | null }>;
}

/**
 * Inline-SVG bar chart of daily spent vs topped-up. Editorial — single ink
 * stroke for spent, accent fill for topped-up. No legend; the headline cards
 * above explain the magnitudes.
 */
export function chartHtml(series: MeStatsResp['dailySeries']): string {
  if (series.length === 0) return '';
  const W = 720;
  const H = 160;
  const PAD = 24;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;
  const max = Math.max(
    1,
    ...series.map((p) => Math.max(p.spentMicroUsdt, p.toppedUpMicroUsdt)),
  );
  const barW = innerW / series.length;
  const bars: string[] = [];
  series.forEach((p, i) => {
    const x = PAD + i * barW + 1;
    const w = Math.max(1, barW - 2);
    if (p.toppedUpMicroUsdt > 0) {
      const h = (p.toppedUpMicroUsdt / max) * innerH;
      const y = PAD + innerH - h;
      bars.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="var(--accent)" opacity="0.85"/>`);
    }
    if (p.spentMicroUsdt > 0) {
      const h = (p.spentMicroUsdt / max) * innerH;
      const y = PAD + innerH - h;
      bars.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="var(--ink)" opacity="0.6"/>`);
    }
  });
  // X-axis labels: first / mid / last, as HTML under the chart. They used to
  // be <text> inside this SVG, which is drawn with preserveAspectRatio="none"
  // at a 720-unit width: on a 335px phone the 10-unit font rendered at ~4.6px
  // and was squashed out of proportion (2026-09-29).
  const labelIdx = [...new Set([0, Math.floor(series.length / 2), series.length - 1])];
  const axis = labelIdx
    .map((i) => `<span>${escapeHtml(series[i]!.date.slice(5))}</span>`)
    .join('');
  return `
<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="dash-analytics__chart" role="img" aria-label="Daily activity">
  <line x1="${PAD}" y1="${PAD + innerH}" x2="${W - PAD}" y2="${PAD + innerH}" stroke="var(--ink-rule)" stroke-width="1"/>
  ${bars.join('')}
</svg>
<div class="dash-analytics__axis mono" aria-hidden="true">${axis}</div>
<div class="dash-analytics__legend">
  <span><i class="dash-analytics__swatch" style="background:var(--accent); opacity:.85"></i>${escapeHtml(t('dash.analytics.legendToppedUp'))}</span>
  <span><i class="dash-analytics__swatch" style="background:var(--ink); opacity:.6"></i>${escapeHtml(t('dash.analytics.legendSpent'))}</span>
</div>`;
}

export async function loadAnalyticsInto(mount: HTMLElement, days: 7 | 30 | 90, esimId?: string): Promise<void> {
  mount.innerHTML = `<div class="dash-loading" data-i18n="dash.analytics.loading">${escapeHtml(t('dash.analytics.loading'))}</div>`;
  try {
    const r = await api.fetchMeStats(days, esimId);
    // Populate the eSIM picker dropdown — visible only when user has 2+
    // non-released eSIMs (single-eSIM users don't need the filter).
    const root = mount.parentElement;
    const pickerWrap = root?.querySelector<HTMLElement>('[data-analytics-picker]');
    const select = root?.querySelector<HTMLSelectElement>('[data-analytics-esim-select]');
    if (pickerWrap && select && r.availableEsims.length >= 2) {
      const allOpt = `<option value="">${escapeHtml(t('dash.analytics.pickerAll'))} (${r.availableEsims.length})</option>`;
      const opts = r.availableEsims.map((e) => {
        // Picker label: prefer MSISDN (user-facing eSIM ID since 2026-05-08).
        // Pending… while RSP backfill is mid-flight; legacy id-tail as deep
        // fallback for very old eSIMs.
        const idLabel = e.msisdn ?? e.displayId ?? e.id.slice(-8).toUpperCase();
        const status = e.status === 'reserved' ? ` · ${t('dash.esims.statusReserved')}` : '';
        return `<option value="${escapeHtml(e.id)}">#${escapeHtml(idLabel)}${escapeHtml(status)}</option>`;
      }).join('');
      select.innerHTML = allOpt + opts;
      select.value = r.selectedEsimId ?? '';
      pickerWrap.style.display = 'inline-flex';
    } else if (pickerWrap) {
      pickerWrap.style.display = 'none';
    }
    if (r.empty) {
      mount.innerHTML = `
<div class="dash-empty">
  <h2>${escapeHtml(t('dash.analytics.emptyTitle'))}</h2>
  <p data-i18n="dash.analytics.emptyBody">${escapeHtml(t('dash.analytics.emptyBody'))}</p>
  <button class="btn btn--accent" data-action="open-topup" data-i18n="dash.esims.emptyCtaGlobal">${escapeHtml(t('dash.esims.emptyCtaGlobal'))}</button>
</div>`;
      return;
    }

    // Variant C: minibar visualisation. Each country row has a sparkline-
    // style horizontal bar showing its share of total spending. Operator
    // requested 2026-05-06 — "богато" feel + scalable to many countries.
    // Top spender = full bar, others scaled relative to it. Falls back
    // to plain row if a single country (no comparison axis).
    const maxMicros = Math.max(0, ...r.byCountry.map((c) => c.microUsdt));
    const totalMicros = r.byCountry.reduce((s, c) => s + c.microUsdt, 0);
    const flagFor = (iso: string): string => {
      if (!iso || iso.length !== 2 || iso === 'XX') return '🌐';
      const codes = [...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
      try { return String.fromCodePoint(...codes); } catch { return '🌐'; }
    };
    // Country name lookup for ISO codes — map the most common ones; for
    // unknowns we fall through to the ISO. Could be replaced with a full
    // import of prices.json countries but that's 192 entries and we
    // already render flags via codepoint trick. Keep small & inline.
    // Country row renderer. For N=1 there's no comparison axis — drop the
    // bar (always 100%) and the share label so the row reads as a clean
    // "🇷🇺 RU · $0.49" instead of a fake-progress UI. For N≥2 keep the
    // mini-bar treatment so users see relative spend at a glance.
    // Operator pass 2026-05-10 (+21): "зачастую стран будет не много —
    // 1-2 строчки".
    const isSoloCountry = r.byCountry.length === 1;
    const renderCountryRow = (c: { countryCode: string; microUsdt: number; mb: number }): string => {
      if (isSoloCountry) {
        return `
          <tr>
            <td>
              <div class="dash-country-row dash-country-row--solo">
                <span class="dash-country-row__label">
                  <span class="dash-country-row__flag" aria-hidden="true">${flagFor(c.countryCode)}</span>
                  <span class="mono">${escapeHtml(c.countryCode)}</span>
                </span>
                <span class="dash-country-row__amount mono">${fmtMoney(c.microUsdt)}</span>
              </div>
            </td>
          </tr>`;
      }
      const widthPct = maxMicros > 0 ? Math.max(2, Math.round((c.microUsdt / maxMicros) * 100)) : 0;
      const sharePct = totalMicros > 0 ? Math.round((c.microUsdt / totalMicros) * 100) : 0;
      const isHot = sharePct >= 40;
      return `
          <tr>
            <td>
              <div class="dash-country-row">
                <span class="dash-country-row__label">
                  <span class="dash-country-row__flag" aria-hidden="true">${flagFor(c.countryCode)}</span>
                  <span class="mono">${escapeHtml(c.countryCode)}</span>
                </span>
                <span class="dash-country-row__bar-wrap">
                  <span class="dash-country-row__bar ${isHot ? 'is-hot' : ''}" style="width:${widthPct}%"></span>
                </span>
                <span class="dash-country-row__amount mono">${fmtMoney(c.microUsdt)}</span>
                <span class="dash-country-row__share">${sharePct}%</span>
              </div>
            </td>
          </tr>`;
    };

    // Show first 2 rows by default. If there are more, render the rest
    // inside a `[data-country-extra]` block (hidden) plus a `[data-country-toggle]`
    // button that flips visibility. Cap at 10 total like the legacy
    // implementation — beyond that, append the "+N more" muted row.
    const COUNTRIES_VISIBLE_DEFAULT = 2;
    const visibleCount = Math.min(r.byCountry.length, COUNTRIES_VISIBLE_DEFAULT);
    const extraSlice = r.byCountry.slice(COUNTRIES_VISIBLE_DEFAULT, 10);
    const overflowCount = Math.max(0, r.byCountry.length - 10);

    const visibleRows = r.byCountry.slice(0, visibleCount).map(renderCountryRow).join('');
    const extraRows = extraSlice.map(renderCountryRow).join('') + (overflowCount > 0
      ? `<tr><td class="dash-sub">${escapeHtml(t('dash.analytics.moreCountries', { n: overflowCount }))}</td></tr>`
      : '');
    const expandRow = extraSlice.length > 0
      ? `<tr data-country-toggle-row><td><button type="button" class="dash-link-btn" data-country-toggle aria-expanded="false">${escapeHtml(t('dash.analytics.showAllCountries', { n: r.byCountry.length }))}</button></td></tr>`
      : '';
    const extraBlock = extraSlice.length > 0
      ? `<tbody data-country-extra hidden>${extraRows}</tbody>`
      : '';

    const byCountryRows = r.byCountry.length
      ? `<tbody>${visibleRows}${expandRow}</tbody>${extraBlock}`
      : `<tbody><tr><td colspan="2" class="dash-sub" data-i18n="dash.analytics.noCountryYet">${escapeHtml(t('dash.analytics.noCountryYet'))}</td></tr></tbody>`;

    const recentRows = r.recentLedger.length
      ? r.recentLedger.map((l) => `
          <tr>
            <td class="mono">${fmtDate(l.createdAt)}</td>
            <td>${formatHistoryType(l.type)}</td>
            <td class="mono ${l.amountMicroUsdt >= 0 ? 'is-positive' : 'is-negative'}">${fmtMoney(l.amountMicroUsdt, { signed: true })}</td>
          </tr>`).join('')
      : `<tr><td colspan="3" class="dash-sub" data-i18n="dash.analytics.noActivityYet">${escapeHtml(t('dash.analytics.noActivityYet'))}</td></tr>`;

    mount.innerHTML = `
<div class="dash-analytics__cards">
  <div class="dash-analytics__card">
    <span class="label" data-i18n="dash.analytics.cardBalance">${escapeHtml(t('dash.analytics.cardBalance'))}</span>
    <span class="value mono">${fmtMoney(r.currentBalanceMicroUsdt)}</span>
  </div>
  <div class="dash-analytics__card">
    <span class="label" data-i18n="dash.analytics.cardSpent">${escapeHtml(t('dash.analytics.cardSpent'))}</span>
    <span class="value mono">${fmtMoney(r.totalSpentMicroUsdt)}</span>
  </div>
  <div class="dash-analytics__card">
    <span class="label" data-i18n="dash.analytics.cardToppedUp">${escapeHtml(t('dash.analytics.cardToppedUp'))}</span>
    <span class="value mono">${fmtMoney(r.totalToppedUpMicroUsdt)}</span>
  </div>
  <div class="dash-analytics__card">
    <span class="label" data-i18n="dash.analytics.cardData">${escapeHtml(t('dash.analytics.cardData'))}</span>
    <span class="value mono">${fmtData(r.dataConsumedMb)}</span>
  </div>
  ${r.totalBonusMicroUsdt > 0 ? `
  <div class="dash-analytics__card">
    <span class="label" data-i18n="dash.analytics.cardBonus">${escapeHtml(t('dash.analytics.cardBonus'))}</span>
    <span class="value mono is-positive">${fmtMoney(r.totalBonusMicroUsdt, { signed: true })}</span>
  </div>` : ''}
</div>

<section class="dash-analytics__section">
  <h3 class="dash-h3">${escapeHtml(t('dash.analytics.dailyTitle'))}</h3>
  ${chartHtml(r.dailySeries)}
</section>

<div class="dash-analytics__split">
  <section class="dash-analytics__section">
    <h3 class="dash-h3">${escapeHtml(t('dash.analytics.byCountryTitle'))}</h3>
    <table class="dash-table dash-table--country">
      ${byCountryRows}
    </table>
  </section>
  <section class="dash-analytics__section">
    <h3 class="dash-h3">${escapeHtml(t('dash.analytics.recentTitle'))}</h3>
    <table class="dash-table">
      <thead><tr><th data-i18n="dash.analytics.colDate">${escapeHtml(t('dash.analytics.colDate'))}</th><th data-i18n="dash.analytics.colType">${escapeHtml(t('dash.analytics.colType'))}</th><th data-i18n="dash.analytics.colAmount">${escapeHtml(t('dash.analytics.colAmount'))}</th></tr></thead>
      <tbody>${recentRows}</tbody>
    </table>
  </section>
</div>`;
  } catch (err) {
    mount.innerHTML = `<div class="dash-empty"><p>${escapeHtml(t('dash.analytics.errorLoad'))} ${escapeHtml((err as Error).message)}</p></div>`;
  }
}

/* ─── History panel (real /api/me/history) ──────────── */
